import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { AUTH_ENABLED } from '../../config/features';

export function TrustBarSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const prefersReducedMotion = useReducedMotion();

  const stats = [
    { value: "10,000+", label: "Research Papers Generated" },
    { value: "95%", label: "User Satisfaction Rate" },
    { value: "50+", label: "Academic Institutions" }
  ];

  return (
    <section ref={ref} className="relative py-20 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.1),transparent_50%)]"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[--text-primary] mb-4">
            Trusted by Researchers Worldwide
          </h2>
          <p className="text-lg sm:text-xl text-[--text-secondary] max-w-3xl mx-auto">
            Join thousands of academics who are accelerating their research writing with Scholar42
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.22, 1, 0.36, 1]
              }}
              className="text-center p-8 bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700"
            >
              <div className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mb-2">
                {stat.value}
              </div>
              <div className="text-sm sm:text-base text-[--text-secondary] font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-gradient-to-r from-primary-600 via-blue-600 to-primary-600 dark:from-primary-700 dark:via-blue-700 dark:to-primary-700 rounded-2xl shadow-2xl p-8 sm:p-12 text-center mb-16"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex justify-center mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <blockquote className="text-lg sm:text-xl md:text-2xl text-white font-medium mb-4 italic max-w-4xl mx-auto">
            "Scholar42 transformed my dissertation writing process. What used to take weeks now takes days. It's like having a research assistant who never sleeps."
          </blockquote>
          <p className="text-blue-100 text-sm sm:text-base font-medium">
            Dr. Sarah Chen, PhD Candidate in Computational Biology
          </p>
        </motion.div>

        <motion.div
          className="text-center"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3 className="text-2xl sm:text-3xl font-bold text-[--text-primary] mb-8">
            Ready to Transform Your Research Writing?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {AUTH_ENABLED ? (
              <Link
                to="/login"
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto"
              >
                Start Your Free Trial
              </Link>
            ) : (
              <span className="inline-block px-8 py-4 text-lg font-semibold text-slate-400 dark:text-slate-500 bg-slate-200 dark:bg-slate-700/60 rounded-xl shadow-sm cursor-not-allowed select-none w-full sm:w-auto text-center">
                Coming Soon
              </span>
            )}
            <Link
              to="/contact"
              className="px-8 py-4 text-lg font-semibold text-[--text-primary] bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
            >
              Contact Sales
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
